import { useEffect, useState } from "react";
import { api, openSecureFile, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Loader2, CheckCircle2, Download, Save, Handshake, FileText, Calendar, Building2, Pencil, X } from "lucide-react";
import { toast } from "sonner";

const CATEGORIE = ["Avvocato", "Perito", "Notaio", "Commercialista", "Consulente del lavoro", "Geometra", "Architetto", "Ingegnere", "Altro"];
const REGIMI = ["Regime Forfettario", "Contabilità semplificata", "Contabilità ordinaria"];
const STATO_STYLE = {
  Proposta: "bg-slate-100 text-slate-700 border-slate-300",
  "Documenti inviati": "bg-amber-100 text-amber-800 border-amber-300",
  Affiliato: "bg-emerald-100 text-emerald-800 border-emerald-300",
  Sospesa: "bg-yellow-100 text-yellow-800 border-yellow-300",
  Cessata: "bg-red-100 text-red-700 border-red-300",
  Revocata: "bg-red-100 text-red-700 border-red-300",  // Iter122k89
};
const statoLabel = (s) => (s === "Affiliato" ? "Convenzionato" : s || "Proposta");

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("it-IT") : "—";

export default function MieConvenzioni() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/credius/professionista/mie-convenzioni");
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Impossibile caricare le convenzioni");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="p-8 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-indigo-800" /></div>;
  const conv = data?.convenzioni || [];
  const obbligatori = data?.campi_obbligatori || [];
  const bloccati = new Set(data?.campi_bloccati || []);

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-5" data-testid="mie-convenzioni-page">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-indigo-900 flex items-center justify-center shrink-0">
          <Handshake className="w-6 h-6 text-[#D4AF5A]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Le mie convenzioni</h1>
          <p className="text-sm text-slate-500">Documenti, stato e scheda anagrafica delle tue convenzioni Credius attive.</p>
        </div>
      </div>

      {conv.length === 0 && (
        <Card className="p-8 text-center text-slate-500 border-2 border-dashed" data-testid="mie-convenzioni-empty">
          Non risultano convenzioni firmate al momento.
        </Card>
      )}

      {conv.map((c) => (
        <Card key={c.id} data-testid={`mie-convenzioni-card-${c.id}`}
          className="p-4 sm:p-5 border-2 border-slate-200 space-y-3">
          {/* Header studio + stato */}
          <div className="flex flex-wrap items-start gap-3">
            <img src={c.studio.logo_url || "/ginius-emblem.png"} alt="logo" className="w-12 h-12 rounded-lg border object-contain bg-white"
              onError={(e) => { e.target.src = "/ginius-emblem.png"; }} />
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-bold text-indigo-700 uppercase tracking-wider flex items-center gap-1">
                <Building2 className="w-3 h-3" /> Studio convenzionato
              </div>
              <div className="text-base font-bold text-slate-800 break-words">{c.studio.ragione_sociale}</div>
              {c.studio.email && <div className="text-xs text-slate-500 break-all">{c.studio.email}</div>}
            </div>
            <span data-testid={`mie-convenzioni-stato-${c.id}`}
              className={`text-xs font-bold px-2 py-1 rounded-full border ${STATO_STYLE[c.stato] || STATO_STYLE.Proposta}`}>
              {statoLabel(c.stato)}
            </span>
          </div>

          {/* Info firma + scadenza */}
          <div className="grid sm:grid-cols-2 gap-2 text-xs text-slate-600 bg-slate-50 rounded-lg p-3 border">
            <div><CheckCircle2 className="w-3.5 h-3.5 inline-block text-emerald-700 mr-1" />
              <b>Firmata il:</b> {fmtDate(c.firma?.signed_at)}</div>
            <div><Calendar className="w-3.5 h-3.5 inline-block text-indigo-600 mr-1" />
              <b>Scadenza:</b> {fmtDate(c.data_scadenza)}</div>
          </div>

          {/* Download PDF firmati + Attestato */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {(c.firma?.docs || []).map((d) => (
              <button key={d.file_id} type="button"
                data-testid={`mie-convenzioni-download-${c.id}-${d.tipo}`}
                onClick={() => openSecureFile(`/credius/professionista/convenzioni/${c.id}/pdf/${d.file_id}`)}
                className="w-full min-w-0 flex items-center gap-2 bg-white border-2 border-emerald-200 rounded-lg px-3 py-2 text-xs sm:text-sm font-bold text-emerald-800 hover:bg-emerald-50">
                <Download className="w-4 h-4 shrink-0" />
                <span className="truncate min-w-0 flex-1 text-left">{d.filename}</span>
              </button>
            ))}
            {c.firma?.attestato_file_id && (
              <button type="button"
                data-testid={`mie-convenzioni-attestato-${c.id}`}
                onClick={() => openSecureFile(`/credius/professionista/convenzioni/${c.id}/pdf/${c.firma.attestato_file_id}`)}
                className="w-full min-w-0 flex items-center gap-2 bg-white border-2 border-indigo-200 rounded-lg px-3 py-2 text-xs sm:text-sm font-bold text-indigo-800 hover:bg-indigo-50">
                <Download className="w-4 h-4 shrink-0" />
                <span className="truncate min-w-0 flex-1 text-left">Attestato di firma</span>
              </button>
            )}
          </div>

          {/* Scheda anagrafica */}
          <SchedaProfessionistaEditor convenzione={c} obbligatori={obbligatori} bloccati={bloccati}
            editing={editingId === c.id}
            onStartEdit={() => setEditingId(c.id)}
            onCancel={() => setEditingId(null)}
            onSaved={() => { setEditingId(null); load(); }} />
        </Card>
      ))}
    </div>
  );
}

const FIELD_META = [
  { k: "nome_cognome_ragione_sociale", lbl: "Nome / Ragione sociale" },
  { k: "codice_fiscale", lbl: "Codice Fiscale" },
  { k: "partita_iva", lbl: "P. IVA" },
  { k: "categoria_professionale", lbl: "Categoria professionale", opts: CATEGORIE },
  { k: "ordine_appartenenza", lbl: "Ordine di appartenenza" },
  { k: "recapiti_telefonici", lbl: "Recapiti telefonici" },
  { k: "recapiti_email_pec", lbl: "Email / PEC" },
  { k: "indirizzo", lbl: "Indirizzo" },
  { k: "cap", lbl: "CAP" },
  { k: "citta", lbl: "Città" },
  { k: "sito_internet", lbl: "Sito internet" },
  { k: "sedi_operative", lbl: "Sedi operative", multiline: true },
  { k: "regime_fiscale", lbl: "Regime fiscale", opts: REGIMI },
  { k: "codice_sdi", lbl: "Codice SDI" },
];

function SchedaProfessionistaEditor({ convenzione, obbligatori, bloccati, editing, onStartEdit, onCancel, onSaved }) {
  const [form, setForm] = useState(convenzione.scheda_professionista || {});
  const [busy, setBusy] = useState(false);
  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    setForm(convenzione.scheda_professionista || {});
  }, [convenzione.id, editing]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    // Client-side check campi obbligatori
    const mancanti = obbligatori.filter((k) => !((form[k] || "").trim()));
    if (mancanti.length > 0) {
      toast.error(`Campi obbligatori mancanti: ${mancanti.map((m) => FIELD_META.find((f) => f.k === m)?.lbl || m).join(", ")}`);
      return;
    }
    setBusy(true);
    try {
      const payload = {};
      FIELD_META.forEach(({ k }) => {
        if (!bloccati.has(k)) payload[k] = form[k] || "";
      });
      payload.obbligo_fatturazione_elettronica = !!form.obbligo_fatturazione_elettronica;
      const r = await api.put(`/credius/professionista/convenzioni/${convenzione.id}/scheda`, payload);
      if (r.data?.cambiamenti > 0) {
        toast.success(`Aggiornati ${r.data.cambiamenti} camp${r.data.cambiamenti === 1 ? "o" : "i"}. Lo Studio è stato notificato.`);
      } else {
        toast.info("Nessun campo è stato modificato.");
      }
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Salvataggio non riuscito");
    } finally { setBusy(false); }
  };

  const inputCls = "w-full h-11 border-2 border-slate-300 rounded-lg px-3 mt-1 disabled:bg-slate-100 disabled:text-slate-600";

  return (
    <div className="border-t-2 border-slate-200 pt-3 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <FileText className="w-4 h-4 text-indigo-700" />
        <div className="font-bold text-slate-800 text-sm">Scheda anagrafica</div>
        {!editing ? (
          <Button size="sm" variant="outline" className="ml-auto h-8 text-xs"
            data-testid={`mie-convenzioni-scheda-edit-${convenzione.id}`}
            onClick={onStartEdit}><Pencil className="w-3.5 h-3.5 mr-1" /> Modifica</Button>
        ) : (
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="outline" className="h-8 text-xs"
              data-testid={`mie-convenzioni-scheda-annulla-${convenzione.id}`}
              onClick={onCancel} disabled={busy}><X className="w-3.5 h-3.5 mr-1" /> Annulla</Button>
            <Button size="sm" className="h-8 text-xs bg-indigo-900 hover:bg-indigo-950"
              data-testid={`mie-convenzioni-scheda-salva-${convenzione.id}`}
              onClick={submit} disabled={busy}>
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
              Salva
            </Button>
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {FIELD_META.map(({ k, lbl, opts, multiline }) => {
          const isBloccato = bloccati.has(k);
          const isObbligatorio = obbligatori.includes(k);
          return (
            <div key={k} className={multiline ? "sm:col-span-2" : ""}>
              <Label className="text-xs uppercase text-slate-600">
                {lbl}
                {isObbligatorio && <span className="text-red-600 ml-1" data-testid={`mie-convenzioni-req-${k}`}>*</span>}
                {isBloccato && <span className="ml-1 text-[10px] font-bold px-1 py-0.5 rounded bg-slate-200 text-slate-600">bloccato</span>}
              </Label>
              {opts ? (
                <select className={inputCls + " bg-white"} disabled={isBloccato || !editing}
                  value={form[k] || ""} onChange={(e) => setF(k, e.target.value)}
                  data-testid={`mie-convenzioni-field-${k}-${convenzione.id}`}>
                  <option value="">— Seleziona —</option>
                  {opts.map((o) => <option key={o}>{o}</option>)}
                </select>
              ) : multiline ? (
                <textarea rows={2} className={inputCls + " h-auto py-2"} disabled={isBloccato || !editing}
                  value={form[k] || ""} onChange={(e) => setF(k, e.target.value)}
                  data-testid={`mie-convenzioni-field-${k}-${convenzione.id}`} />
              ) : (
                <input className={inputCls} disabled={isBloccato || !editing}
                  value={form[k] || ""} onChange={(e) => setF(k, e.target.value)}
                  data-testid={`mie-convenzioni-field-${k}-${convenzione.id}`} />
              )}
            </div>
          );
        })}
        <label className="sm:col-span-2 flex items-center gap-2 text-sm text-slate-700 mt-2">
          <input type="checkbox" disabled={!editing}
            checked={!!form.obbligo_fatturazione_elettronica}
            onChange={(e) => setF("obbligo_fatturazione_elettronica", e.target.checked)} />
          Obbligo di fatturazione elettronica
        </label>
      </div>
    </div>
  );
}
